import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle, Copy, KeyRound } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { useCreateUser } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import type { CreateUserPayload, CreateUserResponse } from '@/api/users';

/**
 * Liste des filiales tenants pour admin_holding (hardcodée car correspond
 * exactement aux 6 projets Supabase dédiés Phase 3 + leadbetonCI legacy).
 */
const TENANT_FILIALES = [
  { filialeKey: 'leadbetonCI',   name: 'Leadbeton CI' },
  { filialeKey: 'textprint',     name: 'Textprint' },
];

const ROLE_LABELS: Record<string, string> = {
  admin_holding: 'Admin Holding (cross-filiale)',
  auditeur:      'Auditeur (lecture seule cross-filiale)',
  dg_filiale:    'DG filiale',
  daf:           'DAF (Directeur Administratif et Financier)',
  manager:       'Manager / Chef de service',
  rh:            'RH (Responsable Ressources Humaines)',
  acheteur:      'Acheteur (achats, fournisseurs, stocks matières)',
  commercial:    'Commercial / Marketing (clients, prospects, devis)',
  chef_equipe:   'Chef d\'équipe (Royal LT)',
};

const ROLES_FOR_HOLDING: string[] = ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'manager', 'rh', 'acheteur', 'commercial', 'chef_equipe'];
const ROLES_FOR_FILIALE: string[] = ['dg_filiale', 'daf', 'manager', 'rh', 'acheteur', 'commercial', 'chef_equipe'];

export const CreateUserDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { user } = useAuthStore();
  const create = useCreateUser();

  const [email, setEmail]           = useState('');
  const [fullName, setFullName]     = useState('');
  const [role, setRole]             = useState<CreateUserPayload['role']>('manager');
  const [targetFiliale, setTarget]  = useState('');
  const [password, setPassword]     = useState('');
  const [phone, setPhone]           = useState('');
  const [error, setError]           = useState<string | null>(null);
  const [created, setCreated]       = useState<CreateUserResponse | null>(null);

  const isAdminHolding = user?.role === 'admin_holding';
  const filiales = TENANT_FILIALES;
  const availableRoles = isAdminHolding ? ROLES_FOR_HOLDING : ROLES_FOR_FILIALE;

  useEffect(() => {
    if (!open) return;
    setEmail(''); setFullName(''); setRole('manager'); setTarget('');
    setPassword(''); setPhone(''); setError(null); setCreated(null);
  }, [open]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await create.mutateAsync({
        email:            email.trim().toLowerCase(),
        fullName:         fullName.trim(),
        role,
        targetFilialeKey: isAdminHolding && targetFiliale ? targetFiliale : undefined,
        password:         password || undefined,
        phone:            phone.trim() || undefined,
      });
      setCreated(result);
    } catch (err) { setError(extractError(err).message); }
  };

  const copyPwd = () => {
    if (created) navigator.clipboard.writeText(`Email: ${created.email}\nMot de passe: ${created.initialPassword}\nFiliale: ${created.tenantKey}`);
  };

  const canSubmit = email.trim() && fullName.trim() && role;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={created ? 'Compte créé avec succès' : 'Créer un compte utilisateur'}
      subtitle={created ? 'Communiquez les identifiants à l\'utilisateur (1 seul affichage)' : 'Ajoute un compte de connexion à la plateforme (Supabase Auth + profil)'}
      size="md"
    >
      {created ? (
        <div className="space-y-3">
          <div style={{
            background: 'var(--p-soft)', border: '1px solid var(--p-light)',
            borderRadius: 'var(--r-sm)', padding: 14, fontSize: 13,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <KeyRound size={16} style={{ color: 'var(--p)' }} />
              <strong style={{ color: 'var(--p-deep)' }}>Identifiants à transmettre :</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 6, fontFamily: 'monospace', fontSize: 12 }}>
              <span style={{ color: 'var(--tx-3)' }}>Email :</span><span>{created.email}</span>
              <span style={{ color: 'var(--tx-3)' }}>Mot de passe :</span><span style={{ fontWeight: 700 }}>{created.initialPassword}</span>
              <span style={{ color: 'var(--tx-3)' }}>Filiale :</span><span>{created.tenantKey}</span>
              <span style={{ color: 'var(--tx-3)' }}>Rôle :</span><span>{ROLE_LABELS[created.role] ?? created.role}</span>
            </div>
            <p style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 8, fontStyle: 'italic' }}>
              ⚠ Demander à l'utilisateur de changer son mot de passe dès le premier login.
            </p>
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
            <button type="button" className="ds-btn ds-btn-g" onClick={copyPwd}><Copy size={14} /> Copier identifiants</button>
            <button type="button" className="ds-btn ds-btn-p" onClick={onClose}>Fermer</button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <AlertTriangle className="inline size-3 mr-1" />{error}
            </div>
          )}

          <Field label="Email *">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="koffi.kone@leadbeton.ci" />
          </Field>

          <Field label="Nom complet *">
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="Koffi KONE" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Rôle *">
              <select value={role} onChange={(e) => setRole(e.target.value as any)} className="ds-fi" style={{ width: '100%' }}>
                {availableRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </Field>
            {isAdminHolding && (
              <Field label="Filiale cible">
                <select value={targetFiliale} onChange={(e) => setTarget(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                  <option value="">— Ma filiale ({user?.filialeCode}) —</option>
                  {filiales.filter((f) => f.filialeKey !== user?.filialeCode).map((f) => (
                    <option key={f.filialeKey} value={f.filialeKey}>{f.name} ({f.filialeKey})</option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Téléphone (optionnel)">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="+225..." />
            </Field>
            <Field label="Mot de passe initial">
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Laisser vide : tiré au sort" minLength={8} />
            </Field>
          </div>

          <p style={{ fontSize: 11, color: 'var(--tx-3)' }}>
            Le compte est créé dans la base Supabase de la filiale cible (isolation multi-tenant).
            L'utilisateur pourra se connecter immédiatement sur app.simgroupci.com avec ces identifiants.
          </p>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
            <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
            <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending}>
              {create.isPending ? 'Création…' : 'Créer le compte'}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
